/**
 * LiveSpanDetail — vista especializada por kind para el drawer de la
 * vista Live (Fase 42 — F6).
 *
 * A diferencia del `SpanDetailPane` (que hace fetch de `linked_call` y
 * `prompt_log` contra la DB), este componente usa el `input`/`output`
 * que viene inline en el evento del bus (`useLiveSession.spanCloses`).
 * Eso es lo que hace al Live realmente "vivo": vemos el prompt que
 * efectivamente se mandó al LLM en este turno, sin esperar a la
 * persistencia.
 *
 * Cuando el span viene del polling (sin bus event), caemos a la vista
 * genérica del `SpanDetailPane` (linked-call + reveal).
 */
import { useState, type ReactElement } from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import type { LiveSpanCloseEvent } from '../../lib/useLiveSession.js';
import { SpanDetailPane, SpanErrorSection } from './SpanDetailPane.js';
import {
  formatDurationMs,
  formatNumber,
  nanoToUsd,
  shortId,
} from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';

const KIND_LABEL: Record<string, string> = {
  llm: 'LLM',
  tool_call: 'Tool call',
  stt: 'STT',
  tts: 'TTS',
  retrieval: 'Memory query',
  guardrail: 'Guardrail',
  pronunciation: 'Pronunciation',
};

export interface LiveSpanDetailProps {
  span: TimelineSpan;
  liveEvent: LiveSpanCloseEvent | undefined;
  onClose: () => void;
}

export function LiveSpanDetail({
  span,
  liveEvent,
  onClose,
}: LiveSpanDetailProps): ReactElement {
  // Sin evento del bus → caemos a la vista genérica que hace fetches.
  // Esto sucede para spans persistidos (post-mortem) o cuando el bus
  // perdió este spanId (reconnect tarde, buffer overflow).
  if (!liveEvent) {
    return <SpanDetailPane span={span} onClose={onClose} />;
  }

  const kindLabel = KIND_LABEL[span.kind] ?? span.kind;
  const cost = preferRawCost(span.providerCostNano, span.costNano);

  return (
    <aside className="panel-detail" data-testid="live-span-detail">
      <div className="pd-head">
        <div className="pd-head-top">
          <span className={`badge b-${span.kind}`}>{kindLabel}</span>
          <span className={`badge ${span.isError ? 'b-error' : 'b-ok'} pd-status`}>
            {span.isError ? span.errorKind ?? 'error' : 'ok'}
          </span>
          <button
            type="button"
            className="pd-close"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="live-span-detail-close"
          >
            ×
          </button>
        </div>
        <div className="pd-name">{span.name}</div>
        {span.parentId ? (
          <div className="pd-sub">parent · {shortId(span.parentId, 12)}</div>
        ) : null}
      </div>

      {/* ── Stats ─────────────────────────────────────────────── */}
      <div className="pd-section">
        <h4 className="pd-section-title">Stats</h4>
        <div className="pd-kv">
          <span className="k">started_at</span>
          <span className="v">{new Date(span.startedAt).toLocaleTimeString('es-AR', { hour12: false })}</span>
          <span className="k">duration</span>
          <span className="v">{formatDurationMs(span.durationMs)}</span>
          <span className="k">cost</span>
          <span className="v">{cost ? nanoToUsd(cost) : '—'}</span>
          <span className="k">span_id</span>
          <span className="v mono">{shortId(span.id, 16)}</span>
        </div>
      </div>

      {/* ── Error payload ─────────────────────────────────────── */}
      <SpanErrorSection span={span} />

      <KindSpecificContent span={span} event={liveEvent} />

      {/* ── Attributes (raw) ──────────────────────────────────── */}
      {span.attributes && Object.keys(span.attributes).length > 0 ? (
        <CollapsibleSection title="Attributes" defaultOpen={false}>
          <JsonView value={span.attributes} />
        </CollapsibleSection>
      ) : null}
    </aside>
  );
}

interface KindSpecificContentProps {
  span: TimelineSpan;
  event: LiveSpanCloseEvent;
}

function KindSpecificContent({ span, event }: KindSpecificContentProps): ReactElement | null {
  switch (span.kind) {
    case 'llm':
      return <LlmDetail span={span} event={event} />;
    case 'tool_call':
      return <ToolCallDetail event={event} />;
    case 'retrieval':
      return <RetrievalDetail event={event} />;
    case 'guardrail':
      return <GuardrailDetail event={event} />;
    case 'stt':
    case 'tts':
    case 'pronunciation':
      return <AudioDetail span={span} event={event} />;
    default:
      return null;
  }
}

/* ── LLM ────────────────────────────────────────────────────── */

interface LlmInput {
  system?: string;
  dynamicSystem?: string;
  messages?: Array<{ role: string; content: string }>;
  tools?: Array<{ name: string; description?: string; input_schema?: unknown }>;
  toolChoice?: unknown;
}
interface LlmOutput {
  text?: string;
  toolCalls?: Array<{ callId: string; name: string; args: unknown }>;
}

function LlmDetail({ span, event }: { span: TimelineSpan; event: LiveSpanCloseEvent }): ReactElement {
  const input = (event.input ?? {}) as LlmInput;
  const output = (event.output ?? {}) as LlmOutput;
  const attrs = span.attributes;
  const provider = typeof attrs['provider'] === 'string' ? attrs['provider'] : null;
  const model = typeof attrs['model'] === 'string' ? attrs['model'] : null;
  const inTokens = typeof attrs['input_tokens'] === 'number' ? attrs['input_tokens'] : null;
  const outTokens = typeof attrs['output_tokens'] === 'number' ? attrs['output_tokens'] : null;
  const cacheRead = typeof attrs['cache_read_tokens'] === 'number' ? attrs['cache_read_tokens'] : null;
  const cacheWrite = typeof attrs['cache_write_tokens'] === 'number' ? attrs['cache_write_tokens'] : null;
  const ttft = typeof attrs['ttft_ms'] === 'number' ? attrs['ttft_ms'] : null;

  return (
    <>
      <div className="pd-section">
        <h4 className="pd-section-title">Model</h4>
        <div className="pd-kv">
          <span className="k">provider</span>
          <span className="v">{provider ?? '—'}</span>
          <span className="k">model</span>
          <span className="v">{model ?? '—'}</span>
          {inTokens !== null ? (
            <>
              <span className="k">in_tokens</span>
              <span className="v">{formatNumber(inTokens)}</span>
            </>
          ) : null}
          {outTokens !== null ? (
            <>
              <span className="k">out_tokens</span>
              <span className="v">{formatNumber(outTokens)}</span>
            </>
          ) : null}
          {cacheRead !== null ? (
            <>
              <span className="k">cache_read</span>
              <span className="v">{formatNumber(cacheRead)}</span>
            </>
          ) : null}
          {cacheWrite !== null ? (
            <>
              <span className="k">cache_write</span>
              <span className="v">{formatNumber(cacheWrite)}</span>
            </>
          ) : null}
          {ttft !== null ? (
            <>
              <span className="k">ttft_ms</span>
              <span className="v">{ttft}</span>
            </>
          ) : null}
        </div>
      </div>

      <PiiWarning />

      {input.system ? (
        <CollapsibleSection title="System prompt" defaultOpen={false}>
          <pre className="pd-json pd-promptlog-text">{input.system}</pre>
        </CollapsibleSection>
      ) : null}

      {input.dynamicSystem ? (
        <CollapsibleSection title="Dynamic context (per-turn)" defaultOpen={false}>
          <pre className="pd-json pd-promptlog-text">{input.dynamicSystem}</pre>
        </CollapsibleSection>
      ) : null}

      {input.messages && input.messages.length > 0 ? (
        <CollapsibleSection title={`Messages (${input.messages.length})`} defaultOpen>
          <div className="pd-messages">
            {input.messages.map((m, i) => (
              <div key={i} className={`pd-msg pd-msg-${m.role}`}>
                <div className="pd-msg-role">{m.role}</div>
                <pre className="pd-msg-content">{m.content}</pre>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      ) : null}

      {input.tools && input.tools.length > 0 ? (
        <CollapsibleSection title={`Tools (${input.tools.length})`} defaultOpen={false}>
          <JsonView value={input.tools} />
        </CollapsibleSection>
      ) : null}

      {output.text ? (
        <div className="pd-section">
          <h4 className="pd-section-title">Response</h4>
          <pre className="pd-json pd-promptlog-text">{output.text}</pre>
        </div>
      ) : null}

      {output.toolCalls && output.toolCalls.length > 0 ? (
        <CollapsibleSection title="Tool calls" defaultOpen>
          <JsonView value={output.toolCalls} />
        </CollapsibleSection>
      ) : null}
    </>
  );
}

/* ── Tool call ──────────────────────────────────────────────── */

function ToolCallDetail({ event }: { event: LiveSpanCloseEvent }): ReactElement {
  const toolName = typeof event.attributes['tool_name'] === 'string'
    ? event.attributes['tool_name']
    : 'unknown';
  const moduleSlug = typeof event.attributes['module_slug'] === 'string'
    ? event.attributes['module_slug']
    : null;
  const success = event.attributes['tool_success'];
  return (
    <>
      <div className="pd-section">
        <h4 className="pd-section-title">Tool</h4>
        <div className="pd-kv">
          <span className="k">tool_name</span>
          <span className="v mono">{toolName}</span>
          {moduleSlug ? (
            <>
              <span className="k">module</span>
              <span className="v">{moduleSlug}</span>
            </>
          ) : null}
          <span className="k">success</span>
          <span className="v">{String(success)}</span>
        </div>
      </div>
      {event.input !== undefined ? (
        <CollapsibleSection title="Args" defaultOpen>
          <JsonView value={event.input} />
        </CollapsibleSection>
      ) : null}
      {event.output !== undefined ? (
        <CollapsibleSection title="Result" defaultOpen>
          <JsonView value={event.output} />
        </CollapsibleSection>
      ) : null}
    </>
  );
}

/* ── Retrieval (memory.query) ───────────────────────────────── */

function RetrievalDetail({ event }: { event: LiveSpanCloseEvent }): ReactElement {
  const a = event.attributes;
  const requester = typeof a['requester_module'] === 'string' ? a['requester_module'] : null;
  const k = typeof a['k'] === 'number' ? a['k'] : null;
  const hits = typeof a['hit_count'] === 'number' ? a['hit_count'] : null;
  const scopes = typeof a['scopes'] === 'string' ? a['scopes'] : null;
  const allowCross = a['allow_cross_module'];
  return (
    <div className="pd-section">
      <h4 className="pd-section-title">Memory query</h4>
      <div className="pd-kv">
        {requester ? (
          <>
            <span className="k">requester</span>
            <span className="v">{requester}</span>
          </>
        ) : null}
        {k !== null ? (
          <>
            <span className="k">k</span>
            <span className="v">{k}</span>
          </>
        ) : null}
        {hits !== null ? (
          <>
            <span className="k">hits</span>
            <span className="v">{hits}</span>
          </>
        ) : null}
        {scopes ? (
          <>
            <span className="k">scopes</span>
            <span className="v mono">{scopes}</span>
          </>
        ) : null}
        <span className="k">cross_module</span>
        <span className="v">{String(Boolean(allowCross))}</span>
      </div>
    </div>
  );
}

/* ── Guardrail ──────────────────────────────────────────────── */

function GuardrailDetail({ event }: { event: LiveSpanCloseEvent }): ReactElement {
  return (
    <div className="pd-section">
      <h4 className="pd-section-title">Guardrail</h4>
      <JsonView value={event.attributes} />
    </div>
  );
}

/* ── STT / TTS / Pronunciation ──────────────────────────────── */

function AudioDetail({ span, event }: { span: TimelineSpan; event: LiveSpanCloseEvent }): ReactElement {
  const a = span.attributes;
  const provider = typeof a['provider'] === 'string' ? a['provider'] : null;
  const model = typeof a['model'] === 'string' ? a['model'] : null;
  const audioS = typeof a['audio_seconds'] === 'number' ? a['audio_seconds'] : null;
  const characters = typeof a['characters'] === 'number' ? a['characters'] : null;
  return (
    <>
      <div className="pd-section">
        <h4 className="pd-section-title">{span.kind.toUpperCase()}</h4>
        <div className="pd-kv">
          {provider ? (
            <>
              <span className="k">provider</span>
              <span className="v">{provider}</span>
            </>
          ) : null}
          {model ? (
            <>
              <span className="k">model</span>
              <span className="v">{model}</span>
            </>
          ) : null}
          {audioS !== null ? (
            <>
              <span className="k">audio_s</span>
              <span className="v">{audioS}</span>
            </>
          ) : null}
          {characters !== null ? (
            <>
              <span className="k">characters</span>
              <span className="v">{characters}</span>
            </>
          ) : null}
        </div>
      </div>
      {event.input !== undefined ? (
        <CollapsibleSection title="Input" defaultOpen={false}>
          <JsonView value={event.input} />
        </CollapsibleSection>
      ) : null}
      {event.output !== undefined ? (
        <CollapsibleSection title="Output" defaultOpen={false}>
          <JsonView value={event.output} />
        </CollapsibleSection>
      ) : null}
    </>
  );
}

/* ── Helpers ────────────────────────────────────────────────── */

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: ReactElement | ReactElement[];
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: CollapsibleSectionProps): ReactElement {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  return (
    <div className="pd-section">
      <h4
        className="pd-section-title"
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: 'pointer' }}
      >
        <span aria-hidden="true" style={{ marginRight: 6 }}>{open ? '▾' : '▸'}</span>
        {title}
      </h4>
      {open ? <div>{children}</div> : null}
    </div>
  );
}

function PiiWarning(): ReactElement {
  return (
    <div className="pd-pii-warn" data-testid="live-pii-warn">
      ⚠ Contenido del usuario — uso interno. Esta vista expone el prompt
      real enviado al proveedor. No persistido en DB.
    </div>
  );
}

function JsonView({ value }: { value: unknown }): ReactElement {
  return (
    <pre
      className="pd-json"
      dangerouslySetInnerHTML={{ __html: highlightJson(value) }}
    />
  );
}

function highlightJson(value: unknown): string {
  let json: string;
  try {
    json = JSON.stringify(value, null, 2);
  } catch {
    json = String(value);
  }
  const escaped = json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(
    /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(\.\d+)?([eE][+-]?\d+)?)/g,
    (match) => {
      if (/^"/.test(match)) {
        return /:$/.test(match)
          ? `<span class="jk">${match}</span>`
          : `<span class="js">${match}</span>`;
      }
      if (/true|false|null/.test(match)) return `<span class="jb">${match}</span>`;
      return `<span class="jn">${match}</span>`;
    },
  );
}
